import { useEffect, useRef, useState } from "react";

import { useCurrentUser } from "@/contexts";
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from "@/utils/common";

export type GlobalSearchMode = "usual" | "superAdmin";

// The selected search mode is remembered on this device so a super admin who
// widened the scope finds it still widened after a reload. Keyed globally (not
// per-tenant) — the mode is a personal preference, not scope-specific state.
// The `tournated:` prefix is added by the localStorage helpers.
const STORAGE_KEY = "globalSearch:mode";

const isGlobalSearchMode = (value: unknown): value is GlobalSearchMode =>
  value === "usual" || value === "superAdmin";

const readStoredMode = (): GlobalSearchMode => {
  const raw = getLocalStorageItem<unknown>(STORAGE_KEY);
  return isGlobalSearchMode(raw) ? raw : "usual";
};

const writeStoredMode = (mode: GlobalSearchMode) => {
  setLocalStorageItem(STORAGE_KEY, mode);
};

/** Removes the persisted global-search mode. Called on logout so a widened super-admin scope never survives into the next session. */
export const clearGlobalSearchMode = () => {
  removeLocalStorageItem(STORAGE_KEY);
};

export const useGlobalSearchMode = () => {
  const currentUser = useCurrentUser();
  const canUseSuperAdminMode = currentUser.role === "superAdmin";

  const [mode, setMode] = useState<GlobalSearchMode>(() => {
    // Only super admins may hold the widened scope; never restore it for anyone
    // else (e.g. a persisted value read after a role downgrade).
    const stored = readStoredMode();
    return stored === "superAdmin" && currentUser.role === "superAdmin" ? "superAdmin" : "usual";
  });

  // Track the account identity so we reset only on a genuine change, not on the
  // initial mount — the initial value already comes from the persisted store.
  const identityRef = useRef(`${currentUser.id}:${currentUser.role}`);

  useEffect(() => {
    const identity = `${currentUser.id}:${currentUser.role}`;
    if (identity === identityRef.current) return;
    identityRef.current = identity;
    // Never retain a widened scope across an account/role change. In particular,
    // this closes the scope immediately if a super admin starts impersonating,
    // and persists the closed scope so it can't be restored on the next reload.
    setMode("usual");
    writeStoredMode("usual");
  }, [currentUser.id, currentUser.role]);

  return {
    mode,
    canUseSuperAdminMode,
    isSuperAdminMode: mode === "superAdmin",
    toggleSuperAdminMode: () => {
      if (!canUseSuperAdminMode) return;
      const next: GlobalSearchMode = mode === "superAdmin" ? "usual" : "superAdmin";
      setMode(next);
      writeStoredMode(next);
    },
  };
};
